import { BellRinging, SpeakerHigh } from '@phosphor-icons/react';
import {
  FESTIVAL_TEXT,
  FESTIVE_DEFAULT_OFF,
  REMINDER_DEFAULTS,
  type FestivalKey,
  type ReminderSettings as Settings,
} from '@shh/shared';
import { Button, ChoiceGroup, MultiChoice, Text, TextField } from '@shh/ui';
import { useEffect, useState } from 'react';
import { updateHousehold } from '../../data/api';
import { useFamily } from '../../data/family';
import { getGong, playGong, setGong, type GongLevel } from '../../lib/gong';
import { locale } from '../../lib/locale';
import { disablePush, enablePush, pushState, type PushState } from '../../lib/push';
import { settingsText } from '../../i18n/settings';

export const remindersOf = (f: ReturnType<typeof useFamily>): Settings => ({
  ...REMINDER_DEFAULTS,
  ...(f.household?.reminders ?? {}),
});

const SaveError = () => (
  <p role="alert" className="font-semibold text-feedback-danger">
    {settingsText.saveError}
  </p>
);
const onOff = [
  { value: 'on' as const, label: settingsText.on },
  { value: 'off' as const, label: settingsText.off },
];

/** Einstellungen → Erinnerungen: the family's defaults, every choice saved at once. */
export function ReminderSettings() {
  const f = useFamily();
  const r = remindersOf(f);
  const [error, setError] = useState(false);
  const save = (patch: Partial<Settings>) => {
    setError(false);
    void updateHousehold(f.householdId, { reminders: { ...r, ...patch } }).catch(() =>
      setError(true),
    );
  };
  return (
    <div className="flex flex-col gap-gap-lg">
      <Text tone="secondary">{settingsText.remindersIntro}</Text>
      <ChoiceGroup<string>
        label={settingsText.timedEvents}
        value={String(r.eventMinutes)}
        onChange={(v) => save({ eventMinutes: Number(v) })}
        choices={[
          { value: '10', label: settingsText.before10 },
          { value: '30', label: settingsText.before30 },
          { value: '60', label: settingsText.before60 },
          { value: '0', label: settingsText.off },
        ]}
      />
      <ChoiceGroup<string>
        label={settingsText.eve}
        value={r.eveHour === null ? 'off' : String(r.eveHour)}
        onChange={(v) => save({ eveHour: v === 'off' ? null : Number(v) })}
        choices={[
          { value: '17', label: settingsText.clock('17') },
          { value: '18', label: settingsText.clock('18') },
          { value: '19', label: settingsText.clock('19') },
          { value: 'off', label: settingsText.off },
        ]}
      />
      <ChoiceGroup<'on' | 'off'>
        label={settingsText.bring}
        description={settingsText.bringHint}
        value={r.bring ? 'on' : 'off'}
        onChange={(v) => save({ bring: v === 'on' })}
        choices={onOff}
      />
      <ChoiceGroup<'on' | 'off'>
        label={settingsText.approvals}
        value={r.approvals ? 'on' : 'off'}
        onChange={(v) => save({ approvals: v === 'on' })}
        choices={onOff}
      />
      <ChoiceGroup<'on' | 'off'>
        label={settingsText.birthdays}
        description={settingsText.birthdaysHint}
        value={r.birthdays ? 'on' : 'off'}
        onChange={(v) => save({ birthdays: v === 'on' })}
        choices={onOff}
      />
      <div className="grid grid-cols-2 gap-gap-md">
        <TextField
          label={settingsText.quietFrom}
          type="time"
          value={r.quietFrom}
          onChange={(v) => v && save({ quietFrom: v })}
        />
        <TextField
          label={settingsText.until}
          type="time"
          value={r.quietTo}
          onChange={(v) => v && save({ quietTo: v })}
        />
      </div>
      <Text size="sm" tone="muted">
        {settingsText.quietHint}
      </Text>
      {error && <SaveError />}
    </div>
  );
}

const PUSH_TEXT: Record<PushState, string> = settingsText.push;

/** Einstellungen → Dieses Gerät: messages on this phone, the gong on this wall. */
export function ThisDeviceSettings() {
  const f = useFamily();
  const [push, setPush] = useState<PushState>();
  const [busy, setBusy] = useState(false);
  const [gong, setGongState] = useState<GongLevel>(getGong);
  useEffect(() => {
    void pushState().then(setPush);
  }, []);
  const isWall = f.role === 'device';
  return (
    <div className="flex flex-col gap-gap-lg">
      {!isWall && push && (
        <div className="flex flex-col gap-gap-sm">
          <span className="font-semibold">{settingsText.pushOnDevice}</span>
          <Text size="sm" tone="secondary" role="status">
            {PUSH_TEXT[push]}
          </Text>
          {(push === 'on' || push === 'off') && (
            <Button
              variant={push === 'on' ? 'secondary' : 'primary'}
              icon={BellRinging}
              className="self-start"
              isDisabled={busy}
              onPress={() => {
                setBusy(true);
                void (
                  push === 'on' ? disablePush(f.householdId) : enablePush(f.householdId, f.meId)
                )
                  .then(setPush, () => setPush('off'))
                  .finally(() => setBusy(false));
              }}
            >
              {push === 'on' ? settingsText.switchOff : settingsText.switchOn}
            </Button>
          )}
        </div>
      )}
      <ChoiceGroup<GongLevel>
        label={settingsText.gong}
        description={settingsText.gongHint}
        value={gong}
        onChange={(v) => {
          setGong(v);
          setGongState(v);
          playGong(v);
        }}
        choices={[
          { value: 'off', label: settingsText.gongLevels.off },
          { value: 'soft', label: settingsText.gongLevels.soft },
          { value: 'medium', label: settingsText.gongLevels.medium },
          { value: 'loud', label: settingsText.gongLevels.loud },
        ]}
      />
      {gong !== 'off' && (
        <Button
          variant="quiet"
          icon={SpeakerHigh}
          className="self-start"
          onPress={() => playGong()}
        >
          {settingsText.listen}
        </Button>
      )}
    </div>
  );
}

/** Einstellungen → Anzeige: festive days on "Heute" and the resting wall. */
export function FestiveSetting() {
  const f = useFamily();
  const festive = f.household?.festive ?? { enabled: true, off: FESTIVE_DEFAULT_OFF };
  const [error, setError] = useState(false);
  const save = (patch: Partial<typeof festive>) => {
    setError(false);
    void updateHousehold(f.householdId, { festive: { ...festive, ...patch } }).catch(() =>
      setError(true),
    );
  };
  const keys = Object.keys(FESTIVAL_TEXT.de.names) as FestivalKey[];
  return (
    <div className="flex flex-col gap-gap-lg">
      <ChoiceGroup<'on' | 'off'>
        label={settingsText.festive}
        description={settingsText.festiveHint}
        value={festive.enabled ? 'on' : 'off'}
        onChange={(v) => save({ enabled: v === 'on' })}
        choices={onOff}
      />
      {festive.enabled && (
        <MultiChoice<FestivalKey>
          label={settingsText.whichFestivals}
          value={keys.filter((k) => !festive.off.includes(k))}
          onChange={(on) => save({ off: keys.filter((k) => !on.includes(k)) })}
          choices={keys.map((k) => ({ value: k, label: FESTIVAL_TEXT[locale].names[k] }))}
        />
      )}
      {error && <SaveError />}
    </div>
  );
}
